import React from "react";
import { FileSearch } from "lucide-react";
import { INSPECTION_SEARCHING_LABEL } from "../constants/ownerPortfolio";
import { useOwnerInspection } from "../hooks/useOwnerInspection";
import { formatInspectionSearchedAt } from "../utils/formatPortfolioDisplay";
import { hasInspectionReport, shouldShowInspectionMessage } from "../utils/inspectionView";

interface InspectionActionProps {
  kenloPropertyId: string;
  accessToken: string | null;
}

const actionButtonClass =
  "inline-flex w-full items-center justify-center rounded-full bg-[#6ea8ff] px-4 py-2.5 text-sm font-bold text-[#0d1117] transition-opacity hover:opacity-90 disabled:cursor-wait disabled:opacity-70";

const secondaryButtonClass =
  "inline-flex w-full items-center justify-center rounded-full border border-white/10 px-4 py-2.5 text-sm font-semibold text-[#8b949e] transition-colors hover:bg-white/5 disabled:cursor-wait disabled:opacity-70";

export default function InspectionAction({
  kenloPropertyId,
  accessToken,
}: InspectionActionProps) {
  const inspection = useOwnerInspection({
    kenloPropertyId,
    accessToken,
    enabled: true,
  });
  const cache = inspection.cache;
  const searchedAtLabel = formatInspectionSearchedAt(cache?.searched_at ?? null);
  const showSearchedAt =
    Boolean(searchedAtLabel) &&
    cache?.status !== "idle" &&
    cache?.status !== "queued" &&
    cache?.status !== "searching";

  if (inspection.status === "loading" && !cache) {
    return (
      <p className="m-0 text-sm text-[#8b949e]" data-testid="inspection-loading">
        {INSPECTION_SEARCHING_LABEL}
      </p>
    );
  }

  if (inspection.status === "error" && !cache) {
    return (
      <div className="flex flex-col items-start gap-2">
        <p className="m-0 text-xs text-[#f85149]">{inspection.actionError}</p>
        <button type="button" className={secondaryButtonClass} onClick={() => void inspection.retry()}>
          Tentar novamente
        </button>
      </div>
    );
  }

  const status = cache?.status ?? "idle";
  const isPolling = status === "queued" || status === "searching";
  const canOpenReport = hasInspectionReport(cache);
  const pollingLabel = cache?.message || INSPECTION_SEARCHING_LABEL;

  return (
    <div className="flex flex-col items-stretch gap-3" data-testid="inspection-panel">
      {shouldShowInspectionMessage(cache) && (
        <p className="m-0 text-sm leading-relaxed text-[#f0f6fc]" data-testid="inspection-message">
          {cache?.message}
        </p>
      )}

      {isPolling ? (
        <button type="button" className={actionButtonClass} disabled>
          {pollingLabel}
        </button>
      ) : canOpenReport ? (
        <button
          type="button"
          className={actionButtonClass}
          disabled={inspection.isOpening}
          onClick={() => void inspection.openReport()}
        >
          <FileSearch className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          {cache?.report?.label}
        </button>
      ) : null}

      {showSearchedAt && (
        <p className="m-0 text-[0.65rem] text-[#6e7681]" data-testid="inspection-searched-at">
          Última busca em {searchedAtLabel}
        </p>
      )}

      {inspection.actionError && (
        <p className="m-0 max-w-xs text-xs text-[#f85149]" data-testid="inspection-action-error">
          {inspection.actionError}
        </p>
      )}
    </div>
  );
}
